import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { Bar } from 'react-chartjs-2';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Chip,
} from '@mui/material';
import useReport from './useReport';
import { formatAmount } from '../../utils/formatAmount';

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtMonth = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toISOString().slice(0, 10);
};

const pct = (part, total) =>
  total > 0 ? `${((part / total) * 100).toFixed(1)}%` : '0%';

/** Distinct colors for each shop (lowercase names in DB: smile, tama) */
const getShopChipStyle = (shopRaw) => {
  const s = (shopRaw || '').toLowerCase().trim();
  if (s === 'tama' || s.startsWith('tama ')) {
    return { backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74', fontWeight: 600 };
  }
  if (s === 'smile' || s.startsWith('smile ')) {
    return { backgroundColor: '#f3e8ff', color: '#6b21a8', border: '1px solid #c4b5fd', fontWeight: 600 };
  }
  return { backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' };
};

const renderShopChips = (shopsStr) => {
  const parts = (shopsStr || '').split(', ').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return '—';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {parts.map((p) => (
        <Chip key={p} label={p} size="small" sx={getShopChipStyle(p)} />
      ))}
    </Box>
  );
};

// column width hints — Last Order uses fixed width + tabular nums so it lines up
const colSx = {
  lastOrder: { width: 112, minWidth: 112, maxWidth: 112, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
};

// ─── modal styles ─────────────────────────────────────────────────────────────

const modalStyle = {
  overlay: { backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1300 },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '92vw',
    maxWidth: 960,
    maxHeight: '92vh',
    overflowY: 'auto',
    borderRadius: 12,
    padding: 28,
  },
};

// ─── table columns ────────────────────────────────────────────────────────────
// All-time columns labeled "All" so they read clearly next to "last month" block

const columns = [
  { key: 'contact',            label: 'Contact',            numeric: false },
  { key: 'shops',              label: 'Shop(s)',            numeric: false },
  { key: 'orderCount',         label: 'Orders',             numeric: true  },
  { key: 'lastOrderDate',      label: 'Last order',         numeric: false },
  { key: 'lastMonthRevenueTHB', label: 'CM revenue (฿)',    numeric: true  },
  { key: 'lastMonthProfitTHB',  label: 'CM profit (฿)',     numeric: true  },
  { key: 'lastMonthMarginPct',  label: 'CM %',              numeric: true  },
  { key: 'totalRevenueTHB',     label: 'All revenue (฿)',   numeric: true  },
  { key: 'profitTHB',           label: 'All profit (฿)',    numeric: true  },
  { key: 'profitPct',           label: 'All %',             numeric: true  },
  { key: 'totalWeightAmount',   label: 'Weight fee (฿)',    numeric: true  },
];

const headerSx = {
  color: 'white !important',
  '& .MuiTableSortLabel-icon': { color: 'white !important' },
};

// ─── chart data builder — dual Y-axis ────────────────────────────────────────

const buildChartData = (monthly) => {
  const sorted = [...monthly].sort((a, b) => new Date(a.month) - new Date(b.month));
  const labels = sorted.map((m) => fmtMonth(m.month));
  return {
    labels,
    datasets: [
      {
        label: 'Revenue (THB)',
        data: sorted.map((m) => Math.round(m.revenueTHB)),
        backgroundColor: 'rgba(79,70,229,0.6)',
        borderColor: 'rgba(79,70,229,1)',
        borderWidth: 1,
        yAxisID: 'yRevenue',
        order: 2,
      },
      {
        label: 'Profit (THB)',
        data: sorted.map((m) => Math.round(m.profitTHB)),
        backgroundColor: 'rgba(5,150,105,0.75)',
        borderColor: 'rgba(5,150,105,1)',
        borderWidth: 2,
        type: 'line',
        yAxisID: 'yProfit',
        tension: 0.3,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(5,150,105,1)',
        order: 1,
      },
    ],
  };
};

const buildChartOptions = (monthly) => {
  const revenues = monthly.map((m) => m.revenueTHB);
  const profits  = monthly.map((m) => m.profitTHB);
  const revMax   = Math.max(...revenues, 1);
  const pMin     = Math.min(...profits, 0);
  const pMax     = Math.max(...profits, 1);

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ฿${Number(ctx.raw).toLocaleString()}`,
        },
      },
    },
    scales: {
      yRevenue: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        max: Math.ceil(revMax * 1.1),
        ticks: {
          color: '#4f46e5',
          callback: (v) => `฿${Number(v).toLocaleString()}`,
        },
        grid: { color: 'rgba(79,70,229,0.08)' },
        title: { display: true, text: 'Revenue', color: '#4f46e5' },
      },
      yProfit: {
        type: 'linear',
        position: 'right',
        min: Math.floor(pMin * 1.15),
        max: Math.ceil(pMax * 1.15),
        ticks: {
          color: '#059669',
          callback: (v) => `฿${Number(v).toLocaleString()}`,
        },
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Profit', color: '#059669' },
      },
    },
  };
};

// ─── summary cards ────────────────────────────────────────────────────────────

const SummaryCards = ({ summary, limit }) => {
  if (!summary) return null;
  const cards = [
    { label: `Top ${limit} customers`,   value: summary.totalCustomers,                       color: '#4e46e5' },
    { label: 'Total orders',             value: formatAmount(summary.totalOrders),             color: '#0f766e' },
    { label: 'All-time revenue',         value: `฿${formatAmount(summary.totalRevenueTHB)}`,   color: '#d97706' },
    { label: 'All-time profit',          value: `฿${formatAmount(summary.totalProfitTHB)}`,     color: '#059669' },
    { label: 'All-time margin',          value: `${summary.profitMarginPct}%`,                 color: '#7c3aed' },
    { label: 'All-time weight fee',      value: `฿${formatAmount(summary.totalWeightAmount)}`, color: '#0369a1' },
  ];
  return (
    <Grid container spacing={2} className="mb-2">
      {cards.map((c) => (
        <Grid item xs={6} sm={4} md={2} key={c.label}>
          <Card style={{ backgroundColor: c.color, color: 'white' }}>
            <CardContent style={{ padding: '12px 16px' }}>
              <Typography variant="body2" style={{ opacity: 0.85 }}>{c.label}</Typography>
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>{c.value}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const LastMonthStrip = ({ summary }) => {
  if (!summary?.lastMonthLabel) return null;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '2px solid #a78bfa',
        background: 'linear-gradient(90deg, rgba(124,58,237,0.07) 0%, rgba(5,150,105,0.09) 100%)',
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1, fontWeight: 700, letterSpacing: 0.3 }}>
        Current month — {summary.lastMonthLabel} (Asia/Bangkok) · top list only
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="textSecondary">Revenue</Typography>
          <Typography variant="h6" sx={{ color: '#4e46e5', fontWeight: 800 }}>
            ฿{formatAmount(Math.round(summary.lastMonthRevenueTHB || 0))}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="textSecondary">Profit</Typography>
          <Typography variant="h6" sx={{ color: '#059669', fontWeight: 800 }}>
            ฿{formatAmount(Math.round(summary.lastMonthProfitTHB || 0))}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="textSecondary">Margin</Typography>
          <Box sx={{ pt: 0.5 }}>
            <Chip
              size="small"
              label={`${(summary.lastMonthMarginPct || 0).toFixed(1)}%`}
              sx={{
                fontWeight: 800,
                backgroundColor: (summary.lastMonthMarginPct || 0) >= 0 ? '#d1fae5' : '#fee2e2',
                color: (summary.lastMonthMarginPct || 0) >= 0 ? '#065f46' : '#991b1b',
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// ─── customer detail modal ────────────────────────────────────────────────────

const CustomerModal = ({ customer, lastMonthLabel, onClose }) => {
  if (!customer) return null;

  const chartData    = buildChartData(customer.monthly);
  const chartOptions = buildChartOptions(customer.monthly);

  const totalRevenue = customer.totalRevenueTHB;
  const totalProfit  = customer.profitTHB;
  const lmRev = customer.lastMonthRevenueTHB || 0;
  const lmPr  = customer.lastMonthProfitTHB || 0;

  return (
    <Modal isOpen={!!customer} onRequestClose={onClose} style={modalStyle} ariaHideApp={false}>
      {/* header */}
      <Box className="flex justify-between items-start mb-3">
        <Box>
          <Typography variant="h5" style={{ fontWeight: 'bold', color: '#4e46e5' }}>
            {customer.contact || customer.smileName || 'Unknown'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, rowGap: 0.5 }}>
            {customer.smileName && (
              <Typography variant="body2" color="textSecondary" component="span">
                @{customer.smileName}
              </Typography>
            )}
            {customer.shops && renderShopChips(customer.shops)}
            <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 0.5 }}>
              {customer.orderCount} orders · Last order: {fmtDate(customer.lastOrderDate)}
            </Typography>
          </Box>
        </Box>
        <Button onClick={onClose} variant="outlined" size="small">Close</Button>
      </Box>

      {/* all-time + last month (same row idea) */}
      <Typography variant="overline" color="textSecondary" display="block" sx={{ mt: 0, mb: 0.5 }}>
        All time
      </Typography>
      <Grid container spacing={2} className="mb-2">
        {[
          { label: 'Revenue',    value: `฿${formatAmount(totalRevenue)}`,              color: '#4e46e5' },
          { label: 'Profit',     value: `฿${formatAmount(totalProfit)}`,               color: '#059669' },
          { label: 'Margin',     value: pct(totalProfit, totalRevenue),                color: '#7c3aed' },
          { label: 'Weight fee', value: `฿${formatAmount(customer.totalWeightAmount)}`,color: '#0369a1' },
        ].map((c) => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Card style={{ backgroundColor: c.color, color: 'white' }}>
              <CardContent style={{ padding: '10px 14px' }}>
                <Typography variant="body2" style={{ opacity: 0.85 }}>{c.label}</Typography>
                <Typography variant="h6" style={{ fontWeight: 'bold' }}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {lastMonthLabel && (
        <>
          <Typography variant="overline" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
            Current month — {lastMonthLabel}
          </Typography>
          <Grid container spacing={2} className="mb-3">
            {[
              { label: 'CM revenue', value: `฿${formatAmount(Math.round(lmRev))}`, color: '#4e46e5' },
              { label: 'CM profit',  value: `฿${formatAmount(Math.round(lmPr))}`,  color: '#059669' },
              { label: 'CM margin',  value: pct(lmPr, lmRev),                     color: '#7c3aed' },
            ].map((c) => (
              <Grid item xs={4} key={c.label}>
                <Card variant="outlined" sx={{ borderColor: '#a78bfa' }}>
                  <CardContent style={{ padding: '10px 14px' }}>
                    <Typography variant="body2" color="textSecondary">{c.label}</Typography>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: c.color }}>{c.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {customer.monthly.length > 0 ? (
        <>
          <Box style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <Box style={{ width: 14, height: 14, borderRadius: 2, backgroundColor: 'rgba(79,70,229,0.6)' }} />
            <Typography variant="caption" style={{ color: '#4f46e5', marginRight: 12 }}>Revenue — left axis</Typography>
            <Box style={{ width: 20, height: 3, backgroundColor: '#059669', borderRadius: 2 }} />
            <Typography variant="caption" style={{ color: '#059669' }}>Profit — right axis</Typography>
          </Box>
          <Box style={{ height: 320 }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        </>
      ) : (
        <Typography color="textSecondary" align="center" className="py-8">
          No monthly data available.
        </Typography>
      )}

      {/* monthly table */}
      {customer.monthly.length > 0 && (
        <TableContainer component={Paper} style={{ marginTop: 16 }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: '#4e46e5' }}>
                {['Month', 'Orders', 'Revenue (THB)', 'Profit (THB)', 'Margin %', 'Weight Fee'].map((h) => (
                  <TableCell key={h} style={{ color: 'white', fontWeight: 'bold' }}
                    align={h === 'Month' ? 'left' : 'right'}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...customer.monthly]
                .sort((a, b) => new Date(b.month) - new Date(a.month))
                .map((m, i) => (
                  <TableRow key={i} style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : 'white' }}>
                    <TableCell>{fmtMonth(m.month)}</TableCell>
                    <TableCell align="right">{m.orderCount}</TableCell>
                    <TableCell align="right" style={{ color: '#4e46e5' }}>
                      {formatAmount(Math.round(m.revenueTHB))}
                    </TableCell>
                    <TableCell align="right"
                      style={{ color: m.profitTHB >= 0 ? '#059669' : '#dc2626', fontWeight: 'bold' }}>
                      {formatAmount(Math.round(m.profitTHB))}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={pct(m.profitTHB, m.revenueTHB)}
                        size="small"
                        style={{
                          backgroundColor: m.profitTHB >= 0 ? '#d1fae5' : '#fee2e2',
                          color: m.profitTHB >= 0 ? '#065f46' : '#991b1b',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatAmount(Math.round(m.weightAmount))}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Modal>
  );
};

// ─── main report ──────────────────────────────────────────────────────────────

const LIMIT_OPTIONS = [100, 200, 500];

const CustomerProfitSummaryReport = () => {
  const { reportData, isLoading, error, getCustomerProfitSummaryReport } = useReport();

  const [limit, setLimit]                       = useState(100);
  const [sortConfig, setSortConfig]             = useState({ key: 'profitTHB', direction: 'desc' });
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    getCustomerProfitSummaryReport(limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const summary      = reportData?.summary   || null;
  const rawCustomers = reportData?.customers || [];

  const customers = useMemo(() =>
    rawCustomers.map((c) => {
      const lmRev = c.lastMonthRevenueTHB || 0;
      const lmPr  = c.lastMonthProfitTHB || 0;
      return {
        ...c,
        profitPct: c.totalRevenueTHB > 0 ? (c.profitTHB / c.totalRevenueTHB) * 100 : 0,
        lastMonthMarginPct: lmRev > 0 ? (lmPr / lmRev) * 100 : 0,
      };
    }),
    [rawCustomers]
  );

  const sorted = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return customers;
    return [...customers].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (key === 'lastOrderDate') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else if (key === 'contact' || key === 'shops') {
        av = (av || '').toLowerCase();
        bv = (bv || '').toLowerCase();
      } else {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      }
      if (av < bv) return direction === 'asc' ? -1 : 1;
      if (av > bv) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [customers, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  return (
    <div className="space-y-4 w-full">
      <SummaryCards summary={summary} limit={limit} />
      <LastMonthStrip summary={summary} />

      {/* toolbar */}
      <Card>
        <CardContent>
          <Box className="flex flex-wrap gap-4 items-center">
            <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
              Show top:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              {LIMIT_OPTIONS.map((n) => (
                <Button
                  key={n}
                  onClick={() => setLimit(n)}
                  variant={limit === n ? 'contained' : 'outlined'}
                  style={limit === n
                    ? { backgroundColor: '#4e46e5', color: 'white', borderColor: '#4e46e5' }
                    : { borderColor: '#4e46e5', color: '#4e46e5' }}
                >
                  {n}
                </Button>
              ))}
            </ButtonGroup>

            <Button
              variant="contained"
              onClick={() => getCustomerProfitSummaryReport(limit)}
              disabled={isLoading}
              style={{ backgroundColor: '#4e46e5', marginLeft: 'auto' }}
            >
              {isLoading ? 'Loading…' : 'Refresh'}
            </Button>

            {error && <Typography color="error" variant="body2">Failed to load report.</Typography>}
            {sorted.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                {sorted.length} customers
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* table */}
      {isLoading && customers.length === 0 ? (
        <div className="flex justify-center py-10"><CircularProgress /></div>
      ) : sorted.length > 0 ? (
        <Paper sx={{ width: '100%', overflow: 'auto' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.numeric ? 'right' : 'left'}
                      style={{ backgroundColor: '#4e46e5', fontWeight: 'bold' }}
                    >
                      <TableSortLabel
                        active={sortConfig.key === col.key}
                        direction={sortConfig.key === col.key ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort(col.key)}
                        sx={headerSx}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row, i) => (
                  <TableRow
                    key={row.customerId || i}
                    hover
                    style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : 'white', cursor: 'pointer' }}
                    onClick={() => setSelectedCustomer(row)}
                  >
                    <TableCell style={{ fontWeight: 'bold', color: '#4e46e5' }}>
                      {row.contact || '-'}
                    </TableCell>
                    <TableCell>{renderShopChips(row.shops)}</TableCell>
                    <TableCell align="right">{row.orderCount}</TableCell>
                    <TableCell align="left" sx={colSx.lastOrder}>
                      {fmtDate(row.lastOrderDate)}
                    </TableCell>
                    <TableCell align="right" style={{ color: '#4e46e5' }}>
                      {row.lastMonthRevenueTHB > 0
                        ? formatAmount(Math.round(row.lastMonthRevenueTHB))
                        : '—'}
                    </TableCell>
                    <TableCell align="right" style={{ fontWeight: 600, color: row.lastMonthProfitTHB >= 0 ? '#059669' : '#dc2626' }}>
                      {row.lastMonthRevenueTHB > 0 || row.lastMonthProfitTHB !== 0
                        ? formatAmount(Math.round(row.lastMonthProfitTHB))
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {row.lastMonthRevenueTHB > 0 ? (
                        <Chip
                          label={`${row.lastMonthMarginPct.toFixed(1)}%`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            backgroundColor: row.lastMonthMarginPct >= 0 ? '#d1fae5' : '#fee2e2',
                            color: row.lastMonthMarginPct >= 0 ? '#065f46' : '#991b1b',
                          }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right" style={{ color: '#4e46e5' }}>
                      {formatAmount(Math.round(row.totalRevenueTHB))}
                    </TableCell>
                    <TableCell align="right"
                      style={{ fontWeight: 'bold', color: row.profitTHB >= 0 ? '#059669' : '#dc2626' }}>
                      {formatAmount(Math.round(row.profitTHB))}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.profitPct.toFixed(1)}%`}
                        size="small"
                        style={{
                          backgroundColor: row.profitPct >= 0 ? '#d1fae5' : '#fee2e2',
                          color: row.profitPct >= 0 ? '#065f46' : '#991b1b',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatAmount(Math.round(row.totalWeightAmount))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        !isLoading && (
          <div className="text-center py-8 text-gray-500">
            <Typography variant="h6">No data found.</Typography>
          </div>
        )
      )}

      <CustomerModal
        customer={selectedCustomer}
        lastMonthLabel={summary?.lastMonthLabel}
        onClose={() => setSelectedCustomer(null)}
      />
    </div>
  );
};

export default CustomerProfitSummaryReport;
